import { Controller } from "@hotwired/stimulus";
import {
  initAddTagsModal,
  showAddTagsModal,
  applyContentTags,
} from "../shared/library_tags";

export default class extends Controller {
  static targets = [
    "libraryBtn",
    "episodeForm",
    "seasonTabs",
    "seasonTabsOuter",
    "seasonEpisodes",
    "backToTop",
  ];
  static values = {
    slug: String,
    contentName: String,
    confirmPrivate: Boolean,
    userContentPrivate: Boolean,
    privateAccount: Boolean,
    tagIds: String,
    hasFriends: Boolean,
  };

  navigate(event) {
    window.location.href = event.currentTarget.dataset.href;
  }

  connect() {
    this.currentItemElement = null;
    this.pendingFormData = null;
    this.pendingFormAction = null;
    this.pendingLibraryButton = null;

    this.boundSharedWatchCompleted = this.handleSharedWatchCompleted.bind(this)
    document.addEventListener("shared-watch:completed", this.boundSharedWatchCompleted)

    this.setupEpisodeModal();
    this.setupStarRating();
    this.setupMassRating();
    this.setupMassRateFriends();
    this.setupResetEpisodes();
    this.setupResetSeason();
    this.setupRemoveFromLibrary();
    this.setupLibraryToggle();
    this.setupPrivateModals();
    this.addTagsModal = initAddTagsModal();
    this.setupSeasonTabs();
  }

  setupSeasonTabs() {
    const wrapper = document.querySelector(".season-tabs-wrapper");
    if (!wrapper) return;

    const activeTab = wrapper.querySelector(".nav-link.active");
    if (activeTab) {
      wrapper.scrollLeft =
        activeTab.offsetLeft -
        wrapper.offsetWidth / 2 +
        activeTab.offsetWidth / 2;
    }

    this.updateTabArrows();
    wrapper.addEventListener("scroll", () => this.updateTabArrows());

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;

    wrapper.addEventListener("mousedown", (e) => {
      isDragging = true;
      startX = e.pageX - wrapper.offsetLeft;
      startScrollLeft = wrapper.scrollLeft;
      wrapper.style.cursor = "grabbing";
      e.preventDefault();
    });

    wrapper.addEventListener("mousemove", (e) => {
      if (!isDragging) return;
      const x = e.pageX - wrapper.offsetLeft;
      wrapper.scrollLeft = startScrollLeft - (x - startX);
    });

    const stopDragging = () => {
      isDragging = false;
      wrapper.style.cursor = "";
    };
    wrapper.addEventListener("mouseup", stopDragging);
    wrapper.addEventListener("mouseleave", stopDragging);
  }

  updateTabArrows() {
    if (!this.hasSeasonTabsOuterTarget) return;
    const wrapper = this.seasonTabsOuterTarget.querySelector(
      ".season-tabs-wrapper",
    );
    if (!wrapper) return;
    const leftBtn = this.seasonTabsOuterTarget.querySelector(
      ".season-tabs-arrow-left",
    );
    const rightBtn = this.seasonTabsOuterTarget.querySelector(
      ".season-tabs-arrow-right",
    );
    if (leftBtn) leftBtn.classList.toggle("d-none", wrapper.scrollLeft <= 0);
    if (rightBtn)
      rightBtn.classList.toggle(
        "d-none",
        wrapper.scrollLeft + wrapper.offsetWidth >= wrapper.scrollWidth - 1,
      );
  }

  scrollTabsLeft() {
    const wrapper = document.querySelector(".season-tabs-wrapper");
    if (wrapper) wrapper.scrollBy({ left: -200, behavior: "smooth" });
  }

  scrollTabsRight() {
    const wrapper = document.querySelector(".season-tabs-wrapper");
    if (wrapper) wrapper.scrollBy({ left: 200, behavior: "smooth" });
  }

  switchSeason(event) {
    const btn = event.currentTarget;
    const season = btn.dataset.season;
    document
      .querySelectorAll(".season-tabs .nav-link")
      .forEach((b) => b.classList.remove("active"));
    btn.classList.add("active");

    const container = this.seasonEpisodesTarget;

    if (!this.seasonCache) this.seasonCache = {};
    if (this.seasonCache[season]) {
      container.innerHTML = this.seasonCache[season];
      this.setupEpisodeClickListeners(
        container.querySelectorAll(".episode-item"),
      );
      return;
    }

    container.innerHTML =
      '<div class="text-center py-4"><div class="spinner-border text-secondary" role="status"><span class="visually-hidden">Loading...</span></div></div>';

    const url = container.dataset.seasonUrl.replace(":season", season);
    fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then((r) => r.text())
      .then((html) => {
        this.seasonCache[season] = html;
        container.innerHTML = html;
        this.setupEpisodeClickListeners(
          container.querySelectorAll(".episode-item"),
        );
      })
      .catch((error) => console.error("Error:", error));
  }

  setupEpisodeModal() {
    const episodeModalEl = document.getElementById("episodeModal");
    if (!episodeModalEl) return;

    this.episodeModal = new bootstrap.Modal(episodeModalEl);
    this.episodeForm = document.getElementById("episodeForm");
    this.watchedCheckbox = document.getElementById("watchedCheckbox");
    this.modalTitle = document.getElementById("episodeModalLabel");
    this.episodeNameDisplay = document.getElementById("episodeNameDisplay");
    this.episodeBadgeDisplay = document.getElementById("episodeBadgeDisplay");


    this.setupEpisodeClickListeners(
      document.querySelectorAll(".episode-item"),
    );

    document.addEventListener("content-tags:buttons-changed", () => {
      this.setupEpisodeClickListeners(
        document.querySelectorAll(".episode-item"),
      );
    });

    document.querySelectorAll(".content-clickable").forEach((item) => {
      item.addEventListener("click", (e) => {
        if (e.target.closest(".shared-watch-btn")) return;
        this.currentItemElement = item;
        this.modalTitle.textContent = item.dataset.contentName;
        if (this.episodeNameDisplay) this.episodeNameDisplay.textContent = "";
        if (this.episodeBadgeDisplay) this.episodeBadgeDisplay.innerHTML = "";
        this.episodeForm.action =
          "/content/" + item.dataset.contentSlug + "/update/";
        this.watchedCheckbox.checked = item.dataset.contentWatched === "true";
        this.setRating(item.dataset.contentRating);
        this.episodeModal.show();
      });

      const sharedWatchBtn = item.querySelector(".shared-watch-btn");
      if (sharedWatchBtn) {
        sharedWatchBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.currentItemElement = item;
          document.dispatchEvent(new CustomEvent("shared-watch:open", {
            detail: {
              contentSlug: item.dataset.contentSlug,
              contentName: item.dataset.contentName,
              tagIds: this.tagIdsValue ? this.tagIdsValue.split(",").filter(Boolean) : [],
            }
          }));
        });
      }
    });

    if (this.episodeForm) {
      this.episodeForm.addEventListener("submit", (e) => {
        e.preventDefault();
        const formData = new FormData(this.episodeForm);

        if (this.shouldShowPrivateModal(formData)) {
          this.pendingFormData = formData;
          this.pendingFormAction = this.episodeForm.action;
          this.episodeModal.hide();
          this.privateRatingModal.show();
          return;
        }

        this.pendingFormData = formData;
        this.pendingFormAction = this.episodeForm.action;
        this.submitRatingForm(null);
      });
    }
  }

  setupEpisodeClickListeners(items) {
    items.forEach((item) => {
      const watchBtn = item.querySelector(".episode-watch-btn");
      if (watchBtn) {
        watchBtn.addEventListener("click", () => {
          this.currentItemElement = item;
          this.modalTitle.textContent = this.contentNameValue;
          if (this.episodeNameDisplay)
            this.episodeNameDisplay.textContent = item.dataset.episodeName;
          if (this.episodeBadgeDisplay) {
            const s = String(item.dataset.seasonNumber).padStart(2, "0");
            const ep = String(item.dataset.episodeNumber).padStart(2, "0");
            this.episodeBadgeDisplay.innerHTML = `<span class="badge badge-episode">S${s} E${ep}</span>`;
          }
          this.episodeForm.action =
            "/episode/" + item.dataset.episodeSlug + "/update/";
          this.watchedCheckbox.checked =
            item.dataset.episodeWatched === "true";
          this.setRating(item.dataset.episodeRating);
          this.episodeModal.show();
        });
      }

      const sharedWatchBtn = item.querySelector(".shared-watch-btn");
      if (sharedWatchBtn) {
        sharedWatchBtn.addEventListener("click", () => {
          this.currentItemElement = item;
          document.dispatchEvent(new CustomEvent("shared-watch:open", {
            detail: {
              episodeSlug: item.dataset.episodeSlug,
              contentName: this.contentNameValue,
              seasonNumber: item.dataset.seasonNumber,
              episodeNumber: item.dataset.episodeNumber,
              episodeName: item.dataset.episodeName,
              tagIds: this.tagIdsValue ? this.tagIdsValue.split(",").filter(Boolean) : [],
            }
          }));
        });
      }
    });
  }

  shouldShowPrivateModal(formData) {
    const rating = formData.get("rating");
    if (!rating) return false;
    if (this.privateAccountValue) return false;
    return this.confirmPrivateValue || this.userContentPrivateValue;
  }

  submitRatingForm(isPrivate) {
    if (!this.pendingFormData || !this.pendingFormAction) return;

    const csrfToken =
      document.querySelector("meta[name='csrf-token']")?.content ||
      document.querySelector("[name=authenticity_token]")?.value ||
      "";
    const submitButton = this.episodeForm.querySelector(
      "button[type='submit']",
    );

    if (isPrivate !== null) {
      this.pendingFormData.set("private", isPrivate ? "true" : "false");
    }

    this.hidePrivateRatingModal();

    submitButton.disabled = true;
    submitButton.innerHTML = '<i class="bi bi-floppy"></i> Saving...';

    fetch(this.pendingFormAction, {
      method: "POST",
      headers: { "X-CSRFToken": csrfToken, "X-CSRF-Token": csrfToken },
      body: this.pendingFormData,
    })
      .then((response) => response.json())
      .then((data) => this.handleFormSuccess(data))
      .catch((error) => console.error("Error:", error))
      .finally(() => {
        submitButton.disabled = false;
        submitButton.innerHTML = '<i class="bi bi-floppy"></i> Save';
        this.pendingFormData = null;
        this.pendingFormAction = null;
      });
  }

  handleFormSuccess(data, { hideEpisodeModal = true } = {}) {
    if (!data.success || !this.currentItemElement) {
      if (hideEpisodeModal) this.episodeModal?.hide();
      return;
    }

    if (data.private !== undefined) {
      this.userContentPrivateValue = data.private;
    }

    const isEpisode =
      this.currentItemElement.classList.contains("episode-item");

    if (isEpisode) {
      this.currentItemElement.dataset.episodeWatched = data.watched.toString();
      this.currentItemElement.dataset.episodeRating = data.rating
        ? data.rating.toString()
        : "";
      if (this.seasonCache)
        delete this.seasonCache[this.currentItemElement.dataset.seasonNumber];

      if (data.watched) {
        const seasonGroup = this.currentItemElement.closest(".season-group");
        if (seasonGroup) {
          const header = seasonGroup.querySelector(
            ".d-flex.align-items-center",
          );
          if (header && !header.querySelector(".btn-reset-season")) {
            const season = seasonGroup.dataset.season;
            const resetBtn = document.createElement("button");
            resetBtn.type = "button";
            resetBtn.className =
              "btn btn-danger btn-action-sm btn-reset-season";
            resetBtn.dataset.season = season;
            resetBtn.dataset.bsToggle = "modal";
            resetBtn.dataset.bsTarget = "#resetSeasonModal";
            resetBtn.innerHTML =
              '<i class="bi bi-arrow-counterclockwise me-1"></i>Reset Season';
            header.appendChild(resetBtn);
          }
        } else if (this.hasSeasonEpisodesTarget) {
          const container = this.seasonEpisodesTarget;
          if (!container.querySelector(".btn-reset-season")) {
            const season = this.currentItemElement.dataset.seasonNumber;
            const wrapper = document.createElement("div");
            wrapper.className = "d-flex justify-content-start mt-2";
            const resetBtn = document.createElement("button");
            resetBtn.type = "button";
            resetBtn.className =
              "btn btn-link text-secondary p-0 btn-reset-season";
            resetBtn.title = "Reset Season";
            resetBtn.dataset.season = season;
            resetBtn.dataset.bsToggle = "modal";
            resetBtn.dataset.bsTarget = "#resetSeasonModal";
            resetBtn.innerHTML =
              '<i class="bi bi-arrow-counterclockwise fs-5"></i>';
            wrapper.appendChild(resetBtn);
            container.appendChild(wrapper);
          }
        }
      }
    } else {
      this.currentItemElement.dataset.contentWatched = data.watched.toString();
      this.currentItemElement.dataset.contentRating = data.rating
        ? data.rating.toString()
        : "";
    }

    const eyeContainer =
      this.currentItemElement.querySelector(".episode-actions") ||
      this.currentItemElement.querySelector(".episode-status");
    if (eyeContainer) {
      const existing =
        eyeContainer.querySelector(".bi-eye") ||
        eyeContainer.querySelector(".bi-eye-slash");
      if (existing) {
        if (data.watched) {
          existing.replaceWith(
            Object.assign(document.createElement("i"), {
              className: "bi bi-eye text-secondary",
              title: "Watched",
            }),
          );
        } else {
          existing.replaceWith(
            Object.assign(document.createElement("i"), {
              className: "bi bi-eye-slash text-secondary",
              title: "Not Watched",
            }),
          );
        }
      }
    }

    const ratingDiv = this.currentItemElement.querySelector(".episode-rating");
    if (data.rating) {
      const starsHtml = this.generateStarsHtml(data.rating);
      if (ratingDiv) {
        ratingDiv.querySelector(".rating-stars").innerHTML = starsHtml;
      } else {
        const statusDiv =
          this.currentItemElement.querySelector(".episode-status");
        if (statusDiv) {
          const newRatingDiv = document.createElement("div");
          newRatingDiv.className = "episode-rating mt-0";
          newRatingDiv.innerHTML =
            '<span class="rating-stars">' + starsHtml + "</span>";
          const actionsDiv = statusDiv.querySelector(".episode-actions");
          statusDiv.insertBefore(newRatingDiv, actionsDiv);
        }
      }
    } else if (ratingDiv) {
      ratingDiv.remove();
    }

    if (data.in_library) {
      this.setLibraryState(true);
    }

    if (isEpisode) {
      this.updateSeriesSummary(data);
      const season = this.currentItemElement.dataset.seasonNumber;
      if (season) this.updateSeasonTabProgress(season);
      this.refreshEpisodesBelow(this.currentItemElement);
    } else {
      this.updateMovieSummary(data);
    }

    if (hideEpisodeModal) this.episodeModal?.hide();
  }

  refreshEpisodesBelow(currentItem) {
    const container = this.hasSeasonEpisodesTarget
      ? this.seasonEpisodesTarget
      : null;
    if (!container) return;

    const season = currentItem.dataset.seasonNumber;
    const currentSlug = currentItem.dataset.episodeSlug;
    if (!season || !currentSlug) return;

    const url = container.dataset.seasonUrl.replace(":season", season);
    fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newItems = Array.from(doc.querySelectorAll(".episode-item"));
        const newCurrentIndex = newItems.findIndex(
          (item) => item.dataset.episodeSlug === currentSlug,
        );
        if (newCurrentIndex === -1) return;

        const replacedItems = [];
        for (let i = newCurrentIndex + 1; i < newItems.length; i++) {
          const newItem = newItems[i];
          const slug = newItem.dataset.episodeSlug;
          const oldItem = container.querySelector(
            `.episode-item[data-episode-slug="${slug}"]`,
          );
          if (oldItem) {
            oldItem.replaceWith(newItem);
            replacedItems.push(newItem);
          }
        }

        if (replacedItems.length > 0) {
          this.setupEpisodeClickListeners(replacedItems);
        }
      })
      .catch((error) => console.error("Error:", error));
  }

  updateSeasonTabProgress(season) {
    const tab = document.querySelector(
      `.season-tabs .nav-link[data-season="${season}"]`,
    );
    if (!tab) return;
    const progressEl = tab.querySelector(".season-tab-progress");
    if (!progressEl) return;
    const total = document.querySelectorAll(
      `.episode-item[data-season-number="${season}"]`,
    ).length;
    const watched = document.querySelectorAll(
      `.episode-item[data-season-number="${season}"][data-episode-watched="true"]`,
    ).length;
    progressEl.textContent = watched + "/" + total;
  }

  updateSeriesSummary(data) {
    if (data.watched_count !== undefined && data.total_count !== undefined) {
      const watchedCountEl = document.querySelector(
        ".content-meta-table .watched-count",
      );
      if (watchedCountEl) {
        watchedCountEl.textContent =
          data.watched_count + " / " + data.total_count + " episodes";
      }
    }

    const titleRatingContainer = document.querySelector(
      ".content-title-rating",
    );
    if (data.average_rating) {
      const avgStarsHtml = this.generateStarsHtml(data.average_rating);
      const displayRating =
        data.average_rating % 1 === 0
          ? Math.floor(data.average_rating)
          : data.average_rating;
      if (titleRatingContainer) {
        titleRatingContainer.querySelector(".rating-stars").innerHTML =
          avgStarsHtml;
        titleRatingContainer.querySelector(".text-muted").textContent =
          "(" + displayRating + "/5)";
      } else {
        const titleText = document.querySelector(".content-title-text");
        if (titleText) {
          const newDiv = document.createElement("div");
          newDiv.className = "content-title-rating mt-1";
          newDiv.innerHTML =
            '<span class="rating-stars">' +
            avgStarsHtml +
            '</span><span class="text-muted ms-1">(' +
            displayRating +
            "/5)</span>";
          titleText.appendChild(newDiv);
        }
      }
    } else if (titleRatingContainer) {
      titleRatingContainer.remove();
    }

    if (
      data.watched_count > 0 &&
      !document.querySelector(".btn-reset-episodes")
    ) {
      const target = this.hasSeasonEpisodesTarget
        ? this.seasonEpisodesTarget
        : null;
      const insertAfter = target ? target.closest(".card") || target : null;
      if (insertAfter) {
        const wrapper = document.createElement("div");
        wrapper.className = "d-flex justify-content-start mb-4";
        const resetBtn = document.createElement("button");
        resetBtn.type = "button";
        resetBtn.className =
          "btn btn-link text-secondary p-0 btn-reset-episodes";
        resetBtn.title = "Reset Episodes";
        resetBtn.dataset.bsToggle = "modal";
        resetBtn.dataset.bsTarget = "#resetEpisodesModal";
        resetBtn.innerHTML =
          '<i class="bi bi-arrow-counterclockwise fs-5"></i>';
        wrapper.appendChild(resetBtn);
        insertAfter.insertAdjacentElement("afterend", wrapper);
      }
    }
  }

  updateMovieSummary(data) {
    const movieSummary = document.querySelector(".movie-summary");
    if (!movieSummary) return;

    const contentMetaTable = document.querySelector(".content-meta-table");
    if (!contentMetaTable) return;

    const findMetaRow = (label) =>
      Array.from(contentMetaTable.querySelectorAll(".content-meta-row")).find(
        (row) => row.querySelector("dt")?.textContent.trim() === label,
      );

    if (data.watched !== undefined) {
      const statusRow = findMetaRow("Status");
      if (statusRow) {
        statusRow.querySelector("dd").textContent = data.watched
          ? "Watched"
          : "Not Watched";
      } else {
        const newRow = document.createElement("div");
        newRow.className = "content-meta-row";
        newRow.innerHTML =
          "<dt>Status</dt><dd>" +
          (data.watched ? "Watched" : "Not Watched") +
          "</dd>";
        contentMetaTable.appendChild(newRow);
      }
    }

    if (data.rating) {
      const starsHtml = this.generateStarsHtml(data.rating);
      const displayRating =
        data.rating % 1 === 0 ? Math.floor(data.rating) : data.rating;
      const titleRatingContainer = document.querySelector(
        ".content-title-rating",
      );
      if (titleRatingContainer) {
        titleRatingContainer.querySelector(".rating-stars").innerHTML =
          starsHtml;
        titleRatingContainer.querySelector(".text-muted").textContent =
          "(" + displayRating + "/5)";
      } else {
        const titleText = document.querySelector(".content-title-text");
        if (titleText) {
          const newDiv = document.createElement("div");
          newDiv.className = "content-title-rating mt-1";
          newDiv.innerHTML =
            '<span class="rating-stars">' +
            starsHtml +
            '</span><span class="text-muted ms-1">(' +
            displayRating +
            "/5)</span>";
          titleText.appendChild(newDiv);
        }
      }
    } else {
      const titleRatingContainer = document.querySelector(
        ".content-title-rating",
      );
      if (titleRatingContainer) titleRatingContainer.remove();
    }
  }

  setupStarRating() {
    const starRatingContainer = document.querySelector(".star-rating-half");
    if (!starRatingContainer) return;

    this.starRatingContainer = starRatingContainer;
    const starContainers =
      starRatingContainer.querySelectorAll(".star-container");

    const updateStarDisplay = (value) => {
      starRatingContainer.classList.add("rating-active");
      starContainers.forEach((container, index) => {
        const starNumber = index + 1;
        container.classList.remove("highlighted", "half-highlighted");
        if (value >= starNumber) {
          container.classList.add("highlighted");
        } else if (value >= starNumber - 0.5) {
          container.classList.add("half-highlighted");
        }
      });
    };

    const clearStarDisplay = () => {
      starRatingContainer.classList.remove("rating-active");
      starContainers.forEach((container) => {
        container.classList.remove("highlighted", "half-highlighted");
      });
    };

    starRatingContainer
      .querySelectorAll("input[type='radio']")
      .forEach((radio) => {
        radio.addEventListener("change", () => {
          if (radio.checked && radio.value) {
            updateStarDisplay(parseFloat(radio.value));
            if (this.watchedCheckbox && !this.watchedCheckbox.checked) {
              this.watchedCheckbox.checked = true;
            }
          }
        });
      });

    starContainers.forEach((container) => {
      container.addEventListener("mouseenter", () => {
        const fullLabel = container.querySelector(".star-full-label");
        if (fullLabel) updateStarDisplay(parseFloat(fullLabel.dataset.value));
      });

      const halfLabel = container.querySelector(".star-half-label");
      const fullLabel = container.querySelector(".star-full-label");

      if (halfLabel) {
        halfLabel.addEventListener("mouseenter", (e) => {
          e.stopPropagation();
          updateStarDisplay(parseFloat(halfLabel.dataset.value));
        });
      }

      if (fullLabel) {
        fullLabel.addEventListener("mouseenter", (e) => {
          e.stopPropagation();
          updateStarDisplay(parseFloat(fullLabel.dataset.value));
        });
      }
    });

    starRatingContainer.addEventListener("mouseleave", () => {
      const checkedRadio = starRatingContainer.querySelector(
        "input[type='radio']:checked",
      );
      if (checkedRadio && checkedRadio.value) {
        updateStarDisplay(parseFloat(checkedRadio.value));
      } else {
        clearStarDisplay();
      }
    });

    const clearBtn = starRatingContainer.querySelector("[data-clear-rating]");
    if (clearBtn) {
      clearBtn.addEventListener("click", (e) => {
        e.preventDefault();
        starRatingContainer
          .querySelectorAll("input[type='radio']")
          .forEach((r) => {
            r.checked = false;
          });
        const noneRadio = document.getElementById("rating-none");
        if (noneRadio) noneRadio.checked = true;
        clearStarDisplay();
      });
    }
  }

  setRating(rating) {
    document.querySelectorAll("input[name='rating']").forEach((radio) => {
      radio.checked = false;
    });

    if (rating) {
      const ratingNum = parseFloat(rating);
      const normalizedRating =
        ratingNum % 1 === 0
          ? Math.floor(ratingNum).toString()
          : ratingNum.toString();
      const ratingStr = normalizedRating.replace(".", "-");
      const ratingRadio = document.getElementById("rating-" + ratingStr);
      if (ratingRadio) {
        ratingRadio.checked = true;
        if (this.starRatingContainer) {
          this.starRatingContainer.classList.add("rating-active");
          const starContainers =
            this.starRatingContainer.querySelectorAll(".star-container");
          starContainers.forEach((container, index) => {
            const starNumber = index + 1;
            container.classList.remove("highlighted", "half-highlighted");
            if (ratingNum >= starNumber) {
              container.classList.add("highlighted");
            } else if (ratingNum >= starNumber - 0.5) {
              container.classList.add("half-highlighted");
            }
          });
        }
      }
    } else {
      const noneRadio = document.getElementById("rating-none");
      if (noneRadio) noneRadio.checked = true;
      if (this.starRatingContainer) {
        this.starRatingContainer.classList.remove("rating-active");
        this.starRatingContainer
          .querySelectorAll(".star-container")
          .forEach((container) => {
            container.classList.remove("highlighted", "half-highlighted");
          });
      }
    }
  }

  handleSharedWatchCompleted(event) {
    this.handleFormSuccess(event.detail, { hideEpisodeModal: false })
  }

  disconnect() {
    document.removeEventListener("shared-watch:completed", this.boundSharedWatchCompleted)
  }

  generateStarsHtml(rating) {
    let html = "";
    for (let i = 1; i <= 5; i++) {
      if (rating >= i) {
        html += '<span class="star-filled">★</span>';
      } else if (rating >= i - 0.5) {
        html += '<span class="star-half">★</span>';
      } else {
        html += '<span class="star-empty">☆</span>';
      }
    }
    return html;
  }

  setupMassRating() {
    const massRatingModalEl = document.getElementById("massRatingModal");
    if (!massRatingModalEl) return;

    this.massRatingModal = new bootstrap.Modal(massRatingModalEl);

    const submitBtn = document.getElementById("massRateButton");
    if (!submitBtn) return;

    const seasonRatings = {};

    const updateButtonState = () => {
      const checkedBoxes = Array.from(
        massRatingModalEl.querySelectorAll(".mass-season-checkbox:checked"),
      );
      submitBtn.disabled = checkedBoxes.length === 0;
    };

    massRatingModalEl
      .querySelectorAll(".mass-season-checkbox")
      .forEach((checkbox) => {
        checkbox.addEventListener("change", updateButtonState);
      });

    massRatingModalEl
      .querySelectorAll(".mass-star-rating")
      .forEach((starContainer) => {
        const season = starContainer.dataset.season;
        const starContainers =
          starContainer.querySelectorAll(".star-container");

        const updateStarDisplay = (value) => {
          starContainer.classList.add("rating-active");
          starContainers.forEach((container, index) => {
            const starNumber = index + 1;
            container.classList.remove("highlighted", "half-highlighted");
            if (value >= starNumber) {
              container.classList.add("highlighted");
            } else if (value >= starNumber - 0.5) {
              container.classList.add("half-highlighted");
            }
          });
        };

        starContainer
          .querySelectorAll("input[type='radio']")
          .forEach((radio) => {
            radio.addEventListener("change", () => {
              if (radio.checked && radio.value) {
                seasonRatings[season] = parseFloat(radio.value);
                updateStarDisplay(seasonRatings[season]);
                const checkbox = massRatingModalEl.querySelector(
                  ".mass-season-checkbox[data-season='" + season + "']",
                );
                if (checkbox && !checkbox.checked) checkbox.checked = true;
                updateButtonState();
              }
            });
          });

        starContainers.forEach((container) => {
          const halfLabel = container.querySelector(".star-half-label");
          const fullLabel = container.querySelector(".star-full-label");

          container.addEventListener("mouseenter", () => {
            if (fullLabel)
              updateStarDisplay(parseFloat(fullLabel.dataset.value));
          });

          if (halfLabel) {
            halfLabel.addEventListener("mouseenter", (e) => {
              e.stopPropagation();
              updateStarDisplay(parseFloat(halfLabel.dataset.value));
            });
          }

          if (fullLabel) {
            fullLabel.addEventListener("mouseenter", (e) => {
              e.stopPropagation();
              updateStarDisplay(parseFloat(fullLabel.dataset.value));
            });
          }
        });

        starContainer.addEventListener("mouseleave", () => {
          if (seasonRatings[season] !== undefined) {
            updateStarDisplay(seasonRatings[season]);
          } else {
            starContainer.classList.remove("rating-active");
            starContainers.forEach((c) =>
              c.classList.remove("highlighted", "half-highlighted"),
            );
          }
        });

        const clearBtn = starContainer.querySelector("[data-clear-rating]");
        if (clearBtn) {
          clearBtn.addEventListener("click", (e) => {
            e.preventDefault();
            delete seasonRatings[season];
            starContainer
              .querySelectorAll("input[type='radio']")
              .forEach((r) => {
                r.checked = false;
              });
            starContainer.classList.remove("rating-active");
            starContainers.forEach((c) =>
              c.classList.remove("highlighted", "half-highlighted"),
            );
          });
        }
      });

    massRatingModalEl.addEventListener("show.bs.modal", () => {
      massRatingModalEl
        .querySelectorAll(".mass-rate-season-row")
        .forEach((row) => {
          const season = row.dataset.season;
          const episodes = Array.from(
            document.querySelectorAll(
              `.episode-item[data-season-number='${season}']`,
            ),
          ).filter((ep) => ep.dataset.episodeFuture !== "true");
          if (episodes.length === 0) return;

          const allWatched = episodes.every(
            (ep) => ep.dataset.episodeWatched === "true",
          );
          if (allWatched) {
            const checkbox = row.querySelector(".mass-season-checkbox");
            if (checkbox) checkbox.checked = true;
          }

          const ratings = episodes
            .map((ep) => ep.dataset.episodeRating)
            .filter((r) => r !== "");
          if (ratings.length === episodes.length && ratings.length > 0) {
            const firstRating = ratings[0];
            if (ratings.every((r) => r === firstRating)) {
              const ratingValue = parseFloat(firstRating);
              seasonRatings[season] = ratingValue;
              const starContainer = row.querySelector(".mass-star-rating");
              if (starContainer) {
                const radio = starContainer.querySelector(
                  `input[value='${ratingValue}']`,
                );
                if (radio) radio.checked = true;
                const starContainers =
                  starContainer.querySelectorAll(".star-container");
                starContainer.classList.add("rating-active");
                starContainers.forEach((container, index) => {
                  const starNumber = index + 1;
                  container.classList.remove("highlighted", "half-highlighted");
                  if (ratingValue >= starNumber) {
                    container.classList.add("highlighted");
                  } else if (ratingValue >= starNumber - 0.5) {
                    container.classList.add("half-highlighted");
                  }
                });
              }
            }
          }
        });
      updateButtonState();
    });

    massRatingModalEl.addEventListener("hidden.bs.modal", () => {
      massRatingModalEl
        .querySelectorAll(".mass-season-checkbox")
        .forEach((cb) => {
          cb.checked = false;
        });
      massRatingModalEl.querySelectorAll(".mass-star-rating").forEach((sc) => {
        sc.classList.remove("rating-active");
        sc.querySelectorAll(".star-container").forEach((c) =>
          c.classList.remove("highlighted", "half-highlighted"),
        );
        sc.querySelectorAll("input[type='radio']").forEach((r) => {
          r.checked = false;
        });
      });
      Object.keys(seasonRatings).forEach((k) => delete seasonRatings[k]);
      submitBtn.disabled = true;
    });

    submitBtn.addEventListener("click", () => {
      const checkedSeasons = [];
      massRatingModalEl
        .querySelectorAll(".mass-season-checkbox:checked")
        .forEach((cb) => {
          const season = cb.dataset.season;
          checkedSeasons.push({
            season_number: parseInt(season),
            rating: seasonRatings[season] ?? null,
          });
        });

      if (checkedSeasons.length === 0) return;

      const hasRating = checkedSeasons.some((s) => s.rating != null);
      if (
        hasRating &&
        !this.privateAccountValue &&
        (this.confirmPrivateValue || this.userContentPrivateValue)
      ) {
        this.pendingMassRateSeasons = checkedSeasons;
        this.massRatingModal.hide();
        this.privateRatingModal.show();
        return;
      }

      this.submitMassRate(checkedSeasons, null, submitBtn);
    });
  }

  submitMassRate(checkedSeasons, isPrivate, submitBtn) {
    this.hidePrivateRatingModal();

    const csrfToken =
      document.querySelector("meta[name='csrf-token']")?.content || "";
    const originalHtml = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.textContent = "Marking...";

    const body = { seasons: checkedSeasons };
    if (isPrivate !== null) body.private = isPrivate;

    fetch("/content/" + this.slugValue + "/mass-rate", {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.massRatingModal.hide();
          this.handleMassRateSuccess(data);
        }
      })
      .catch((error) => console.error("Error:", error))
      .finally(() => {
        submitBtn.innerHTML = originalHtml;
      });
  }

  handleMassRateSuccess(data) {
    if (data.season_counts) {
      data.season_counts.forEach(({ season, watched, total }) => {
        const tab = document.querySelector(
          `.season-tabs .nav-link[data-season="${season}"]`,
        );
        if (!tab) return;
        const progressEl = tab.querySelector(".season-tab-progress");
        if (progressEl) progressEl.textContent = watched + "/" + total;
      });
    }

    this.setLibraryState(true);
    this.updateSeriesSummary(data);

    const container = this.hasSeasonEpisodesTarget
      ? this.seasonEpisodesTarget
      : null;
    if (!container) return;

    const activeTab = document.querySelector(".season-tabs .nav-link.active");
    const firstItem = container.querySelector(".episode-item");
    const season = activeTab
      ? activeTab.dataset.season
      : firstItem
        ? firstItem.dataset.seasonNumber
        : null;
    if (!season) return;

    const url = container.dataset.seasonUrl.replace(":season", season);
    fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then((r) => r.text())
      .then((html) => {
        container.innerHTML = html;
        this.setupEpisodeClickListeners(
          container.querySelectorAll(".episode-item"),
        );
      })
      .catch((error) => console.error("Error:", error));
  }

  setupMassRateFriends() {
    const modalEl = document.getElementById("massRateFriendsModal");
    if (!modalEl) return;

    this.massRateFriendsModal = new bootstrap.Modal(modalEl);
    const submitBtn = document.getElementById("massRateFriendsButton");
    if (!submitBtn) return;

    const ratings = {};
    const ratingKey = (userId, season) => `${userId}:${season}`;

    const updateButtonState = () => {
      submitBtn.disabled = !modalEl.querySelector(".mfr-season-checkbox:checked");
    };

    modalEl.querySelectorAll(".mfr-season-checkbox").forEach((checkbox) => {
      checkbox.addEventListener("change", updateButtonState);
    });

    modalEl.querySelectorAll(".mfr-star-rating").forEach((starContainer) => {
      this.setupMfrStarRating(modalEl, starContainer, ratings, ratingKey, updateButtonState);
    });

    modalEl.addEventListener("show.bs.modal", () => {
      this.prefillMfrCurrentUser(modalEl, ratings, ratingKey);
      updateButtonState();
    });

    modalEl.addEventListener("hidden.bs.modal", () => {
      this.resetMfrModal(modalEl, ratings);
      submitBtn.disabled = true;
    });

    submitBtn.addEventListener("click", () => {
      const targets = this.collectMfrTargets(modalEl, ratings, ratingKey);
      if (Object.keys(targets).length === 0) return;
      this.submitMassRateFriends(targets, modalEl, submitBtn);
    });
  }

  setupMfrStarRating(modalEl, starContainer, ratings, ratingKey, updateButtonState) {
    const userId = starContainer.dataset.userId;
    const season = starContainer.dataset.season;
    const key = ratingKey(userId, season);
    const starContainers = starContainer.querySelectorAll(".star-container");

    const updateStarDisplay = (value) => {
      starContainer.classList.add("rating-active");
      starContainers.forEach((container, index) => {
        const starNumber = index + 1;
        container.classList.remove("highlighted", "half-highlighted");
        if (value >= starNumber) {
          container.classList.add("highlighted");
        } else if (value >= starNumber - 0.5) {
          container.classList.add("half-highlighted");
        }
      });
    };

    starContainer.querySelectorAll("input[type='radio']").forEach((radio) => {
      radio.addEventListener("change", () => {
        if (!radio.checked || !radio.value) return;
        ratings[key] = parseFloat(radio.value);
        updateStarDisplay(ratings[key]);
        const checkbox = modalEl.querySelector(
          `.mfr-season-checkbox[data-user-id='${userId}'][data-season='${season}']`,
        );
        if (checkbox && !checkbox.checked) checkbox.checked = true;
        updateButtonState();
      });
    });

    starContainers.forEach((container) => {
      const halfLabel = container.querySelector(".star-half-label");
      const fullLabel = container.querySelector(".star-full-label");
      container.addEventListener("mouseenter", () => {
        if (fullLabel) updateStarDisplay(parseFloat(fullLabel.dataset.value));
      });
      if (halfLabel) {
        halfLabel.addEventListener("mouseenter", (event) => {
          event.stopPropagation();
          updateStarDisplay(parseFloat(halfLabel.dataset.value));
        });
      }
      if (fullLabel) {
        fullLabel.addEventListener("mouseenter", (event) => {
          event.stopPropagation();
          updateStarDisplay(parseFloat(fullLabel.dataset.value));
        });
      }
    });

    starContainer.addEventListener("mouseleave", () => {
      if (ratings[key] !== undefined) {
        updateStarDisplay(ratings[key]);
      } else {
        starContainer.classList.remove("rating-active");
        starContainers.forEach((container) =>
          container.classList.remove("highlighted", "half-highlighted"),
        );
      }
    });

    const clearBtn = starContainer.querySelector("[data-clear-rating]");
    if (clearBtn) {
      clearBtn.addEventListener("click", (event) => {
        event.preventDefault();
        delete ratings[key];
        starContainer.querySelectorAll("input[type='radio']").forEach((radio) => {
          radio.checked = false;
        });
        starContainer.classList.remove("rating-active");
        starContainers.forEach((container) =>
          container.classList.remove("highlighted", "half-highlighted"),
        );
      });
    }
  }

  prefillMfrCurrentUser(modalEl, ratings, ratingKey) {
    const currentSection = modalEl.querySelector(".mfr-target[data-current-user]");
    if (!currentSection) return;
    const userId = currentSection.dataset.userId;

    currentSection.querySelectorAll(".mfr-season-row").forEach((row) => {
      const season = row.dataset.season;
      const episodes = Array.from(
        document.querySelectorAll(`.episode-item[data-season-number='${season}']`),
      ).filter((episode) => episode.dataset.episodeFuture !== "true");
      if (episodes.length === 0) return;

      const allWatched = episodes.every(
        (episode) => episode.dataset.episodeWatched === "true",
      );
      if (allWatched) {
        const checkbox = row.querySelector(".mfr-season-checkbox");
        if (checkbox) checkbox.checked = true;
      }

      const episodeRatings = episodes
        .map((episode) => episode.dataset.episodeRating)
        .filter((rating) => rating !== "");
      if (
        episodeRatings.length === episodes.length &&
        episodeRatings.length > 0 &&
        episodeRatings.every((rating) => rating === episodeRatings[0])
      ) {
        const ratingValue = parseFloat(episodeRatings[0]);
        const key = ratingKey(userId, season);
        ratings[key] = ratingValue;
        const starContainer = row.querySelector(".mfr-star-rating");
        if (!starContainer) return;
        const radio = starContainer.querySelector(`input[value='${ratingValue}']`);
        if (radio) radio.checked = true;
        const starContainers = starContainer.querySelectorAll(".star-container");
        starContainer.classList.add("rating-active");
        starContainers.forEach((container, index) => {
          const starNumber = index + 1;
          container.classList.remove("highlighted", "half-highlighted");
          if (ratingValue >= starNumber) {
            container.classList.add("highlighted");
          } else if (ratingValue >= starNumber - 0.5) {
            container.classList.add("half-highlighted");
          }
        });
      }
    });
  }

  resetMfrModal(modalEl, ratings) {
    modalEl.querySelectorAll(".mfr-season-checkbox").forEach((checkbox) => {
      checkbox.checked = false;
    });
    modalEl.querySelectorAll(".mfr-star-rating").forEach((starContainer) => {
      starContainer.classList.remove("rating-active");
      starContainer.querySelectorAll(".star-container").forEach((container) =>
        container.classList.remove("highlighted", "half-highlighted"),
      );
      starContainer.querySelectorAll("input[type='radio']").forEach((radio) => {
        radio.checked = false;
      });
    });
    Object.keys(ratings).forEach((key) => delete ratings[key]);
  }

  collectMfrTargets(modalEl, ratings, ratingKey) {
    const targets = {};
    let index = 0;
    modalEl.querySelectorAll(".mfr-target").forEach((targetEl) => {
      const userId = targetEl.dataset.userId;
      const seasons = [];
      targetEl.querySelectorAll(".mfr-season-checkbox:checked").forEach((checkbox) => {
        const season = checkbox.dataset.season;
        seasons.push({
          season_number: parseInt(season),
          rating: ratings[ratingKey(userId, season)] ?? null,
        });
      });
      if (seasons.length > 0) {
        targets[String(index)] = { user_id: userId, seasons };
        index += 1;
      }
    });
    return targets;
  }

  submitMassRateFriends(targets, modalEl, submitBtn) {
    const csrfToken =
      document.querySelector("meta[name='csrf-token']")?.content || "";
    const url = modalEl.dataset.mfrUrl;
    const originalHtml = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.textContent = "Marking...";

    fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ targets }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.massRateFriendsModal.hide();
          this.handleMassRateSuccess(data);
        }
      })
      .catch((error) => console.error("Error:", error))
      .finally(() => {
        submitBtn.innerHTML = originalHtml;
      });
  }

  setupResetEpisodes() {
    const confirmResetCheckbox = document.getElementById(
      "confirmResetCheckbox",
    );
    const confirmResetButton = document.getElementById("confirmResetButton");

    if (!confirmResetCheckbox || !confirmResetButton) return;

    confirmResetCheckbox.addEventListener("change", () => {
      confirmResetButton.disabled = !confirmResetCheckbox.checked;
    });

    confirmResetButton.addEventListener("click", () => {
      const contentSlug = confirmResetButton.dataset.contentSlug;
      const csrfToken =
        document.querySelector("meta[name='csrf-token']")?.content ||
        document.querySelector("[name=authenticity_token]")?.value ||
        "";
      const modal = bootstrap.Modal.getInstance(
        document.getElementById("resetEpisodesModal"),
      );

      confirmResetButton.disabled = true;
      confirmResetButton.textContent = "Resetting...";

      fetch("/content/" + contentSlug + "/reset-episodes/", {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.reset) {
            modal.hide();
            document.querySelectorAll(".episode-item").forEach((item) => {
              item.dataset.episodeWatched = "false";
              item.dataset.episodeRating = "";
              const eyeTarget = item.querySelector(".episode-actions") || item.querySelector(".episode-status");
              if (eyeTarget) {
                const icon = eyeTarget.querySelector(".bi-eye") || eyeTarget.querySelector(".bi-eye-slash");
                if (icon)
                  icon.replaceWith(
                    Object.assign(document.createElement("i"), {
                      className: "bi bi-eye-slash text-secondary",
                      title: "Not Watched",
                    }),
                  );
              }
              const ratingDiv = item.querySelector(".episode-rating");
              if (ratingDiv) ratingDiv.remove();
            });
            const watchedCountEl = document.querySelector(
              ".content-meta-table .watched-count",
            );
            if (watchedCountEl) {
              const totalMatch = watchedCountEl.textContent.match(/\/ (\d+)/);
              if (totalMatch)
                watchedCountEl.textContent =
                  "0 / " + totalMatch[1] + " episodes";
            }
            const avgRatingDiv = document.querySelector(
              ".content-title-rating",
            );
            if (avgRatingDiv) avgRatingDiv.remove();
            document
              .querySelectorAll(".btn-reset-season, .btn-reset-episodes")
              .forEach((btn) => {
                const wrapper = btn.parentElement;
                if (
                  wrapper &&
                  wrapper.classList.contains("align-items-center")
                ) {
                  btn.remove();
                } else {
                  wrapper.remove();
                }
              });
            document
              .querySelectorAll(".season-group .collapse:not(.show)")
              .forEach((collapseEl) => {
                bootstrap.Collapse.getOrCreateInstance(collapseEl).show();
              });
            document
              .querySelectorAll(".season-tabs .nav-link")
              .forEach((tab) => {
                const progressEl = tab.querySelector(".season-tab-progress");
                if (!progressEl) return;
                const match = progressEl.textContent.match(/\d+\/(\d+)/);
                if (match) progressEl.textContent = "0/" + match[1];
              });
          }
        })
        .catch((error) => console.error("Error:", error))
        .finally(() => {
          confirmResetButton.disabled = true;
          confirmResetButton.textContent = "Reset All Episodes";
          confirmResetCheckbox.checked = false;
        });
    });
  }

  setupResetSeason() {
    const confirmResetSeasonCheckbox = document.getElementById(
      "confirmResetSeasonCheckbox",
    );
    const confirmResetSeasonButton = document.getElementById(
      "confirmResetSeasonButton",
    );
    const resetSeasonForm = document.getElementById("resetSeasonForm");

    if (!confirmResetSeasonCheckbox || !confirmResetSeasonButton) return;

    confirmResetSeasonCheckbox.addEventListener("change", () => {
      confirmResetSeasonButton.disabled = !confirmResetSeasonCheckbox.checked;
    });

    document.addEventListener("click", (e) => {
      const btn = e.target.closest(".btn-reset-season");
      if (btn) {
        const season = btn.dataset.season;
        document.getElementById("resetSeasonNumber").textContent = season;
        document.getElementById("confirmSeasonNumber").textContent = season;
        this.pendingResetSeason = season;
        confirmResetSeasonCheckbox.checked = false;
        confirmResetSeasonButton.disabled = true;
      }
    });

    resetSeasonForm.addEventListener("submit", (e) => {
      e.preventDefault();
      const season = this.pendingResetSeason;
      if (!season) return;

      const csrfToken =
        document.querySelector("meta[name='csrf-token']")?.content ||
        document.querySelector("[name=authenticity_token]")?.value ||
        "";
      const modal = bootstrap.Modal.getInstance(
        document.getElementById("resetSeasonModal"),
      );

      confirmResetSeasonButton.disabled = true;

      fetch("/content/" + this.slugValue + "/reset-season/" + season + "/", {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.reset) {
            modal.hide();

            const container = this.hasSeasonEpisodesTarget
              ? this.seasonEpisodesTarget
              : null;
            const seasonWatchedBefore = container
              ? container.querySelectorAll(
                  ".episode-item[data-episode-watched='true']",
                ).length
              : 0;

            if (container) {
              container.querySelectorAll(".episode-item").forEach((item) => {
                item.dataset.episodeWatched = "false";
                item.dataset.episodeRating = "";
                const eyeIcon = item.querySelector(".bi-eye");
                if (eyeIcon) {
                  eyeIcon.classList.remove("bi-eye");
                  eyeIcon.classList.add("bi-eye-slash");
                  eyeIcon.title = "Not Watched";
                }
                const badge = item.querySelector(".badge");
                if (badge) {
                  badge.classList.remove("bg-secondary");
                  badge.classList.add("bg-secondary");
                  badge.innerHTML =
                    '<i class="bi bi-eye-slash me-1"></i>Not Watched';
                }
                const ratingDiv = item.querySelector(".episode-rating");
                if (ratingDiv) ratingDiv.remove();
              });
              const resetBtn = container.querySelector(".btn-reset-season");
              if (resetBtn) {
                const wrapper = resetBtn.parentElement;
                if (
                  wrapper &&
                  wrapper.classList.contains("align-items-center")
                ) {
                  resetBtn.remove();
                } else {
                  wrapper.remove();
                }
              }
            }

            this.updateSeasonTabProgress(season);

            const watchedCountEl = document.querySelector(
              ".content-meta-table .watched-count",
            );
            if (watchedCountEl) {
              const match = watchedCountEl.textContent.match(/(\d+) \/ (\d+)/);
              if (match) {
                const remaining = Math.max(
                  0,
                  parseInt(match[1]) - seasonWatchedBefore,
                );
                watchedCountEl.textContent =
                  remaining + " / " + match[2] + " episodes";
                if (remaining === 0)
                  document
                    .querySelectorAll(".btn-reset-episodes")
                    .forEach((btn) => {
                      const wrapper = btn.parentElement;
                      if (
                        wrapper &&
                        wrapper.classList.contains("align-items-center")
                      ) {
                        btn.remove();
                      } else {
                        wrapper.remove();
                      }
                    });
              }
            }
          }
        })
        .catch((error) => console.error("Error:", error))
        .finally(() => {
          confirmResetSeasonButton.disabled = true;
          confirmResetSeasonCheckbox.checked = false;
          this.pendingResetSeason = null;
        });
    });
  }

  setupRemoveFromLibrary() {
    const confirmRemoveCheckbox = document.getElementById(
      "confirmRemoveCheckbox",
    );
    const confirmRemoveButton = document.getElementById("confirmRemoveButton");

    if (!confirmRemoveCheckbox || !confirmRemoveButton) return;

    confirmRemoveCheckbox.addEventListener("change", () => {
      confirmRemoveButton.disabled = !confirmRemoveCheckbox.checked;
    });

    confirmRemoveButton.addEventListener("click", () => {
      const contentSlug = confirmRemoveButton.dataset.contentSlug;
      const csrfToken =
        document.querySelector("meta[name='csrf-token']")?.content ||
        document.querySelector("[name=authenticity_token]")?.value ||
        "";
      const modal = bootstrap.Modal.getInstance(
        document.getElementById("removeFromLibraryModal"),
      );

      confirmRemoveButton.disabled = true;
      confirmRemoveButton.textContent = "Removing...";

      fetch("/content/" + contentSlug + "/remove-from-library", {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.removed) {
            modal.hide();
            this.setLibraryState(false);

            const tagsCard = document.querySelector(".content-tags-card");
            if (tagsCard) tagsCard.remove();

            document
              .querySelectorAll(".btn-reset-episodes, .btn-reset-season")
              .forEach((btn) => {
                const wrapper = btn.parentElement;
                if (
                  wrapper &&
                  wrapper.classList.contains("align-items-center")
                ) {
                  btn.remove();
                } else {
                  wrapper.remove();
                }
              });

            this.clearAllWatchedAndRatings();
          }
        })
        .catch((error) => console.error("Error:", error))
        .finally(() => {
          confirmRemoveButton.disabled = true;
          confirmRemoveButton.textContent = "Remove";
          confirmRemoveCheckbox.checked = false;
        });
    });
  }

  clearAllWatchedAndRatings() {
    const episodeItems = document.querySelectorAll(".episode-item");
    const contentCard = document.querySelector(".content-clickable");

    if (episodeItems.length > 0) {
      episodeItems.forEach((item) => {
        item.dataset.episodeWatched = "false";
        item.dataset.episodeRating = "";
        const eyeEl = item.querySelector(".episode-actions .bi-eye") ||
                     item.querySelector(".episode-actions .bi-eye-slash");
        if (eyeEl)
          eyeEl.replaceWith(
            Object.assign(document.createElement("i"), {
              className: "bi bi-eye-slash text-secondary",
              title: "Not Watched",
            }),
          );
        const ratingDiv = item.querySelector(".episode-rating");
        if (ratingDiv) ratingDiv.remove();
      });
      const watchedCountEl = document.querySelector(
        ".content-meta-table .watched-count",
      );
      if (watchedCountEl) {
        const totalMatch = watchedCountEl.textContent.match(/\/ (\d+)/);
        if (totalMatch)
          watchedCountEl.textContent = "0 / " + totalMatch[1] + " episodes";
      }
      const avgRatingDiv = document.querySelector(
        ".content-meta-table .content-meta-row:has(.rating-stars)",
      );
      if (avgRatingDiv) avgRatingDiv.remove();
      document.querySelectorAll(".season-tab-progress").forEach((el) => {
        const totalMatch = el.textContent.match(/\/(\d+)/);
        if (totalMatch) el.textContent = "0/" + totalMatch[1];
      });
      const titleRating = document.querySelector(".content-title-rating");
      if (titleRating) titleRating.remove();
    } else if (contentCard) {
      contentCard.dataset.contentWatched = "false";
      contentCard.dataset.contentRating = "";
      const icon = contentCard.querySelector(".episode-status .bi-eye") ||
                   contentCard.querySelector(".episode-status .bi-eye-slash");
      if (icon)
        icon.replaceWith(
          Object.assign(document.createElement("i"), {
            className: "bi bi-eye-slash text-secondary",
            title: "Not Watched",
          }),
        );
      const ratingDiv = contentCard.querySelector(".episode-rating");
      if (ratingDiv) ratingDiv.remove();
      const contentMetaTable = document.querySelector(".content-meta-table");
      if (contentMetaTable) {
        const findMetaRow = (label) =>
          Array.from(
            contentMetaTable.querySelectorAll(".content-meta-row"),
          ).find(
            (row) => row.querySelector("dt")?.textContent.trim() === label,
          );
        const statusRow = findMetaRow("Status");
        if (statusRow)
          statusRow.querySelector("dd").textContent = "Not Watched";
        const ratingRow = findMetaRow("Rating");
        if (ratingRow) ratingRow.remove();
      }
    }
  }

  setLibraryState(inLibrary) {
    const addBtn = document.querySelector(".library-toggle-btn");
    const removeBtn = document.querySelector(".library-remove-btn");
    if (addBtn) addBtn.classList.toggle("d-none", inLibrary);
    if (removeBtn) removeBtn.classList.toggle("d-none", !inLibrary);
  }

  setupLibraryToggle() {
    const addBtn = document.querySelector(".library-toggle-btn");
    if (addBtn) {
      addBtn.addEventListener("click", () => {
        if (this.confirmPrivateValue) {
          this.pendingLibraryButton = addBtn;
          this.privateLibraryModal.show();
          return;
        }
        this.pendingLibraryButton = addBtn;
        if (this.hasFriendsValue) {
          this.showAddTagsForLibrary();
          return;
        }
        this.addToLibrary(null);
      });
    }

    const removeBtn = document.querySelector(".library-remove-btn");
    if (removeBtn) {
      removeBtn.addEventListener("click", () => {
        const removeModal = new bootstrap.Modal(
          document.getElementById("removeFromLibraryModal"),
        );
        removeModal.show();
      });
    }
  }

  addToLibrary(isPrivate, skipEvent = false) {
    if (!this.pendingLibraryButton) return;

    const button = this.pendingLibraryButton;
    const contentSlug = button.dataset.contentSlug;
    const csrfToken =
      document.querySelector("meta[name='csrf-token']")?.content ||
      document.querySelector("[name=authenticity_token]")?.value ||
      "";

    if (this.privateLibraryModal) this.privateLibraryModal.hide();

    button.classList.add("disabled");

    const params = [];
    if (isPrivate !== null) params.push("private=" + isPrivate);
    if (skipEvent) params.push("skip_event=true");
    const body = params.join("&");

    fetch("/content/" + contentSlug + "/add-to-library", {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/x-www-form-urlencoded",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: body,
    })
      .then((response) => response.json())
      .then((data) => {
        button.classList.remove("disabled");
        if (data.added) {
          if (data.restored) {
            window.location.reload();
            return;
          }
          this.setLibraryState(true);
          if (data.private !== undefined) {
            this.userContentPrivateValue = data.private;
          }
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        button.classList.remove("disabled");
      })
      .finally(() => {
        this.pendingLibraryButton = null;
      });
  }

  showAddTagsForLibrary() {
    showAddTagsModal(
      this.addTagsModal,
      this.slugValue,
      this.contentNameValue,
      {
        onApply: (checkboxes) => {
          this.addTagsModal.hide();
          this.addToLibrary(null, true);
          applyContentTags(this.slugValue, checkboxes).then(() =>
            window.location.reload(),
          );
        },
        onSkip: () => this.addToLibrary(null),
      },
    );
  }

  setupPrivateModals() {
    const privateRatingModalEl = document.getElementById("privateRatingModal");
    if (privateRatingModalEl) {
      this.privateRatingModal = new bootstrap.Modal(privateRatingModalEl);
      this.privateRatingModalShowing = false;
      privateRatingModalEl.addEventListener("show.bs.modal", () => {
        this.privateRatingModalShowing = true;
      });
      privateRatingModalEl.addEventListener("shown.bs.modal", () => {
        this.privateRatingModalShowing = false;
      });
      const massSubmitBtn = document.getElementById("massRateButton");
      document.getElementById("savePublicBtn").addEventListener("click", () => {
        if (this.pendingMassRateSeasons) {
          const seasons = this.pendingMassRateSeasons;
          this.pendingMassRateSeasons = null;
          this.submitMassRate(seasons, false, massSubmitBtn);
        } else {
          this.submitRatingForm(false);
        }
      });
      document
        .getElementById("savePrivateBtn")
        .addEventListener("click", () => {
          if (this.pendingMassRateSeasons) {
            const seasons = this.pendingMassRateSeasons;
            this.pendingMassRateSeasons = null;
            this.submitMassRate(seasons, true, massSubmitBtn);
          } else {
            this.submitRatingForm(true);
          }
        });
    }

    const privateLibraryModalEl = document.getElementById(
      "privateLibraryModal",
    );
    if (privateLibraryModalEl) {
      this.privateLibraryModal = new bootstrap.Modal(privateLibraryModalEl);
      document
        .getElementById("addPublicBtn")
        .addEventListener("click", () => this.addToLibrary(false));
      document
        .getElementById("addPrivateBtn")
        .addEventListener("click", () => this.addToLibrary(true));
    }
  }

  hidePrivateRatingModal() {
    if (!this.privateRatingModal) return;
    if (this.privateRatingModalShowing) {
      this.privateRatingModal._element.addEventListener(
        "shown.bs.modal",
        () => this.privateRatingModal.hide(),
        { once: true },
      );
    } else {
      this.privateRatingModal.hide();
    }
  }

  scrollToTop(event) {
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}
